import { BOARD } from "../data/board";

export const MAP = {
  minScale: 0.25,
  maxScale: 2.75,
  initialScale: 1,
  edgePad: 72,
  zoomStep: 1.2,
  wheelZoomIntensity: 0.01,
  panThreshold: 5,
};

const LINE_DELTA = 16;
const PAGE_DELTA = 800;

export const INTERACTIVE_SELECTOR = [
  "a",
  "button",
  "summary",
  "input",
  "textarea",
  "select",
  "label",
  "[role='button']",
  "[data-cursor='hover']",
].join(",");

export const clamp = (value, a, b) => {
  const min = Math.min(a, b);
  const max = Math.max(a, b);
  return Math.max(min, Math.min(max, value));
};

/** Opening zoom: full size on desktop, pulled back on phones so the dossier card fits the screen. */
export const homeScale = (vw) => clamp(vw / 560, 0.66, MAP.initialScale);

/** Zoom at which the whole board is in view. */
export const fitScale = (vw, vh) =>
  clamp(Math.min(vw / BOARD.width, vh / BOARD.height) * 0.97, MAP.minScale, MAP.maxScale);

export const hypot = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

export const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

export const supportsCssZoom = () =>
  typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("zoom", "1");

export const isZoomWheel = () => true;

export const isEditableTarget = (target) => {
  if (!target || !target.closest) return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
};

export const isInteractiveTarget = (target) => {
  if (!target || !target.closest) return false;
  return Boolean(target.closest(INTERACTIVE_SELECTOR));
};

export const normalizeWheelDelta = (event) => {
  let { deltaX, deltaY } = event;
  if (event.deltaMode === 1) {
    deltaX *= LINE_DELTA;
    deltaY *= LINE_DELTA;
  } else if (event.deltaMode === 2) {
    deltaX *= PAGE_DELTA;
    deltaY *= PAGE_DELTA;
  }
  return { deltaX, deltaY };
};

export const wheelZoomFactor = (deltaY) =>
  clamp(Math.exp(-deltaY * MAP.wheelZoomIntensity), 0.86, 1.16);

export const panFromWheel = (event, deltaX, deltaY) => {
  if (event.shiftKey && Math.abs(deltaY) >= Math.abs(deltaX)) {
    return { dx: deltaY, dy: 0 };
  }
  return { dx: deltaX, dy: deltaY };
};

export const screenToBoard = (px, py, x, y, scale) => ({
  x: (px - x) / scale,
  y: (py - y) / scale,
});

export const panBounds = (scale, vw, vh) => {
  const boardW = BOARD.width * scale;
  const boardH = BOARD.height * scale;
  const pad = MAP.edgePad;

  let minX;
  let maxX;
  let minY;
  let maxY;

  if (boardW <= vw) {
    const centered = (vw - boardW) / 2;
    minX = Math.min(centered, pad);
    maxX = Math.max(centered, vw - boardW - pad);
  } else {
    minX = vw - boardW - pad;
    maxX = pad;
  }

  if (boardH <= vh) {
    const centered = (vh - boardH) / 2;
    minY = Math.min(centered, pad);
    maxY = Math.max(centered, vh - boardH - pad);
  } else {
    minY = vh - boardH - pad;
    maxY = pad;
  }

  return { minX, maxX, minY, maxY };
};

export const clampPan = (x, y, scale, vw, vh) => {
  const bounds = panBounds(scale, vw, vh);
  return {
    x: clamp(x, bounds.minX, bounds.maxX),
    y: clamp(y, bounds.minY, bounds.maxY),
  };
};

export const zoomToward = ({ x, y, scale, nextScale, px, py, vw, vh }) => {
  const s = clamp(nextScale, MAP.minScale, MAP.maxScale);
  const point = screenToBoard(px, py, x, y, scale);
  return clampPan(px - point.x * s, py - point.y * s, s, vw, vh);
};

export const viewForPoint = (boardX, boardY, scale, vw, vh) => {
  const s = clamp(scale, MAP.minScale, MAP.maxScale);
  return clampPan(vw / 2 - boardX * s, vh / 2 - boardY * s, s, vw, vh);
};

export const applyBoardZoom = (node, nextScale, useCssZoom) => {
  if (!node) return;
  if (useCssZoom) {
    node.style.zoom = String(nextScale);
    node.style.transform = "";
    return;
  }
  node.style.zoom = "";
  node.style.transform = `scale(${nextScale})`;
};
